import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNoIndex } from '@/hooks/useSEO';
import { getStashedAffiliateCode } from '@/components/AffiliateRefTracker';

export default function Register() {
    useNoIndex('Create your account — Linguistic Quest');
    const navigate = useNavigate();
    const { register } = useAuth();
    const loc = useLocation();
    const params = new URLSearchParams(loc.search);
    const intendedTier = params.get('tier') || '';
    const refCode = params.get('ref') || '';
    // Affiliate attribution — set by AffiliateRefTracker on the first visit
    // that carried ?ref=. Survives 30 days across tabs / sessions.
    const affiliateCode = getStashedAffiliateCode();

    const [form, setForm] = useState({
        display_name: '', email: '', password: '',
        country_code: 'GB', is_adult_confirmed: false,
        marketing_email_consent: false,
        consent_terms: false,
        referral_code: refCode,
    });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (!form.consent_terms) { setErr('Please accept the Terms and Privacy Policy.'); return; }
        if (!form.is_adult_confirmed) { setErr('Please confirm you are 18 or older.'); return; }
        setBusy(true);
        try {
            await register({
                email: form.email, password: form.password, display_name: form.display_name,
                country_code: form.country_code, is_adult_confirmed: true,
                marketing_email_consent: form.marketing_email_consent,
                referral_code: form.referral_code || undefined,
                affiliate_code: affiliateCode || undefined,
            });
            navigate('/onboarding/add-child' + (intendedTier ? `?tier=${intendedTier}` : ''));
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Registration failed');
        } finally { setBusy(false); }
    };

    return (
        <main className="max-w-md mx-auto px-6 py-16" data-testid="page-register">
            <h1 className="font-display text-4xl font-semibold text-lq-ink-black">Start free.</h1>
            <p className="mt-2 text-sm text-lq-ink-500">Parents register first, you'll add children and set up consent in the next step.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Your name</span>
                    <input required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="register-name" />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Email</span>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="register-email" />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Password <span className="text-lq-ink-500">(min 8 characters)</span></span>
                    <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="register-password" />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Country</span>
                    <select value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="register-country">
                        {['GB','US','ZW','ZA','KE','NG','TZ','FR','PT','CA','AU','DE','IE','MZ','SN'].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label className="flex gap-2 items-start">
                    <input type="checkbox" checked={form.is_adult_confirmed} onChange={(e) => setForm({ ...form, is_adult_confirmed: e.target.checked })} className="mt-1" data-testid="register-adult" />
                    <span className="text-sm text-lq-ink-900">I confirm I am 18 or older and am the parent or legal guardian of any child profiles I create.</span>
                </label>
                <label className="flex gap-2 items-start">
                    <input type="checkbox" checked={form.consent_terms} onChange={(e) => setForm({ ...form, consent_terms: e.target.checked })} className="mt-1" data-testid="register-terms" />
                    <span className="text-sm text-lq-ink-900">I agree to the <Link to="/legal/terms" className="text-lq-purple-700 underline">Terms</Link> and <Link to="/legal/privacy" className="text-lq-purple-700 underline">Privacy Policy</Link>.</span>
                </label>
                <label className="flex gap-2 items-start">
                    <input type="checkbox" checked={form.marketing_email_consent} onChange={(e) => setForm({ ...form, marketing_email_consent: e.target.checked })} className="mt-1" data-testid="register-marketing" />
                    <span className="text-sm text-lq-ink-500">Send me occasional product emails (new languages, tips). Unsubscribe any time.</span>
                </label>
                {err && <div className="text-sm text-lq-error" role="alert">{err}</div>}
                <button disabled={busy} className="w-full bg-lq-purple-500 text-white font-semibold py-3 rounded-full hover:bg-lq-purple-700 disabled:opacity-60" data-testid="register-submit">
                    {busy ? 'Creating account…' : 'Create account'}
                </button>
                <p className="text-sm text-lq-ink-500 text-center">Already have an account? <Link to="/login" className="text-lq-purple-700 underline">Sign in</Link></p>
            </form>
        </main>
    );
}
